import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Save as SaveIcon,
  Tune as TuneIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import {
  createOCRAdaptation,
  deleteOCRAdaptation,
  fetchDefaultInstructionSet,
  fetchOCRAdaptations,
  toggleOCRAdaptation,
  updateOCRAdaptation,
} from '../../store/slices/ocrAdaptationSlice';
import ocrAdaptationService from '../../services/ocrAdaptationService';
import { OCRAdaptation, OCRAdaptationTestResult } from '../../types/ocrAdaptation';
import { useMobileLayout } from '../../hooks/useMobileLayout';

const OCRAdaptations: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { adaptations, defaultInstructionSet, loading, error } = useSelector((state: RootState) => state.ocrAdaptations);
  const { isSmallMobile, spacing } = useMobileLayout();

  const [selectedAdaptation, setSelectedAdaptation] = useState<OCRAdaptation | null>(null);
  const [vendorName, setVendorName] = useState('');
  const [instructionSet, setInstructionSet] = useState('');
  const [priority, setPriority] = useState(50);
  const [isActive, setIsActive] = useState(true);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [matchWholeWord, setMatchWholeWord] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [testResult, setTestResult] = useState<OCRAdaptationTestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    dispatch(fetchOCRAdaptations({}));
    dispatch(fetchDefaultInstructionSet());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedAdaptation && !instructionSet && defaultInstructionSet) {
      setInstructionSet(defaultInstructionSet);
    }
  }, [defaultInstructionSet, instructionSet, selectedAdaptation]);

  const showMessage = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const resetForm = () => {
    setSelectedAdaptation(null);
    setVendorName('');
    setInstructionSet(defaultInstructionSet);
    setPriority(50);
    setIsActive(true);
    setCaseSensitive(false);
    setMatchWholeWord(false);
    setTestResult(null);
  };

  const loadAdaptation = (adaptation: OCRAdaptation) => {
    setSelectedAdaptation(adaptation);
    setVendorName(adaptation.vendorName);
    setInstructionSet(adaptation.instructionSet);
    setPriority(adaptation.priority);
    setIsActive(adaptation.isActive);
    setCaseSensitive(adaptation.caseSensitive);
    setMatchWholeWord(adaptation.matchWholeWord);
    setTestResult(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] || null;
    setFile(nextFile);
  };

  const handleRunTest = async () => {
    if (!file) {
      showMessage('Vyberte PDF soubor pro test OCR adaptace.', 'error');
      return;
    }

    setTesting(true);
    try {
      const result = await ocrAdaptationService.testAdaptation({
        file,
        instructionSet: instructionSet || defaultInstructionSet,
        vendorName,
      });
      setTestResult(result);
      setVendorName(vendorName || result.detectedVendorName);
      setInstructionSet(result.draftInstructionSet || instructionSet || defaultInstructionSet);
      showMessage('Test OCR adaptace dokončen.');
    } catch (err: any) {
      showMessage(err.response?.data?.details || err.message || 'Test OCR adaptace selhal.', 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    const payload = {
      vendorName: vendorName.trim(),
      instructionSet: instructionSet.trim(),
      isActive,
      caseSensitive,
      matchWholeWord,
      priority,
    };

    if (!payload.vendorName || !payload.instructionSet) {
      showMessage('Vyplňte název dodavatele a instrukční sadu.', 'error');
      return;
    }

    try {
      if (selectedAdaptation) {
        const updated = await dispatch(updateOCRAdaptation({ id: selectedAdaptation.id, data: payload })).unwrap();
        setSelectedAdaptation(updated);
        showMessage('OCR adaptace byla upravena.');
      } else {
        const created = await dispatch(createOCRAdaptation(payload)).unwrap();
        setSelectedAdaptation(created);
        showMessage('OCR adaptace byla uložena.');
      }
    } catch (err: any) {
      showMessage(String(err || 'Uložení OCR adaptace selhalo.'), 'error');
    }
  };

  const handleToggle = async (adaptation: OCRAdaptation) => {
    try {
      await dispatch(toggleOCRAdaptation(adaptation.id)).unwrap();
      showMessage('Stav OCR adaptace byl změněn.');
    } catch (err: any) {
      showMessage(String(err || 'Změna stavu selhala.'), 'error');
    }
  };

  const handleDelete = async (adaptation: OCRAdaptation) => {
    try {
      await dispatch(deleteOCRAdaptation(adaptation.id)).unwrap();
      if (selectedAdaptation?.id === adaptation.id) {
        resetForm();
      }
      showMessage('OCR adaptace byla smazána.');
    } catch (err: any) {
      showMessage(String(err || 'Smazání OCR adaptace selhalo.'), 'error');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: spacing.sm }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: spacing.md }}>
        <TuneIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant={isSmallMobile ? 'h5' : 'h4'} fontWeight="bold">
          OCR Adaptace
        </Typography>
      </Box>

      <Paper sx={{ p: spacing.sm, mb: spacing.md, backgroundColor: 'info.light', borderLeft: 4, borderColor: 'info.main' }}>
        <Typography variant="body2" color="text.secondary">
          Nahrajte vzorovou fakturu, spusťte OCR a AI rozpoznání, upravte celou instrukční sadu pro konkrétního dodavatele a opakujte test, dokud výsledek nebude správný. Při běžném zpracování se po OCR hledá název dodavatele a nalezená adaptace nahradí standardní instrukce.
        </Typography>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: spacing.md }}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '360px 1fr' }, gap: spacing.md }}>
        <Paper sx={{ p: spacing.sm }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Uložené adaptace</Typography>
            <Button size="small" onClick={resetForm}>Nová</Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List dense>
              {adaptations.map(adaptation => (
                <ListItem
                  key={adaptation.id}
                  selected={selectedAdaptation?.id === adaptation.id}
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Switch
                        edge="end"
                        checked={adaptation.isActive}
                        onChange={() => handleToggle(adaptation)}
                        size="small"
                      />
                      <IconButton edge="end" onClick={() => handleDelete(adaptation)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                  sx={{ cursor: 'pointer', pr: 9 }}
                  onClick={() => loadAdaptation(adaptation)}
                >
                  <ListItemText
                    primary={adaptation.vendorName}
                    secondary={`Priorita ${adaptation.priority} · použito ${adaptation.usageCount || 0}x`}
                  />
                </ListItem>
              ))}
              {adaptations.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Zatím nejsou uložené žádné OCR adaptace.
                </Typography>
              )}
            </List>
          )}
        </Paper>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          <Paper sx={{ p: spacing.sm }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Testovací pracovní plocha
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 160px 160px' }, gap: 2, mb: 2 }}>
              <TextField
                label="Název dodavatele"
                value={vendorName}
                onChange={(event) => setVendorName(event.target.value)}
                helperText="Tento text se bude hledat v OCR výstupu."
                fullWidth
              />
              <TextField
                label="Priorita"
                type="number"
                value={priority}
                onChange={(event) => setPriority(Number(event.target.value))}
                inputProps={{ min: 0, max: 100 }}
              />
              <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                PDF
                <input hidden accept="application/pdf" type="file" onChange={handleFileChange} />
              </Button>
            </Box>

            {file && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Soubor: {file.name}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />}
                label="Aktivní"
              />
              <FormControlLabel
                control={<Switch checked={caseSensitive} onChange={(event) => setCaseSensitive(event.target.checked)} />}
                label="Rozlišovat velikost písmen"
              />
              <FormControlLabel
                control={<Switch checked={matchWholeWord} onChange={(event) => setMatchWholeWord(event.target.checked)} />}
                label="Pouze celé slovo"
              />
            </Box>

            <TextField
              label="Instrukční sada pro extrakci"
              value={instructionSet}
              onChange={(event) => setInstructionSet(event.target.value)}
              minRows={14}
              multiline
              fullWidth
              helperText="Ukládá se jako plná náhrada standardních instrukcí pro tohoto dodavatele."
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={testing ? <CircularProgress size={18} color="inherit" /> : <PlayIcon />}
                onClick={handleRunTest}
                disabled={testing}
              >
                {testing ? 'Testuji...' : 'Spustit test'}
              </Button>
              <Button variant="contained" color="success" startIcon={<SaveIcon />} onClick={handleSave}>
                {selectedAdaptation ? 'Uložit změny' : 'Uložit adaptaci'}
              </Button>
              <Button variant="outlined" onClick={() => setInstructionSet(defaultInstructionSet)}>
                Vrátit standardní instrukce
              </Button>
            </Box>
          </Paper>

          {testResult && (
            <Paper sx={{ p: spacing.sm }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <Typography variant="h6">Výsledek testu</Typography>
                {testResult.matchedAdaptation && (
                  <Chip label={`Nalezena adaptace: ${testResult.matchedAdaptation.vendorName}`} color="primary" size="small" />
                )}
                {testResult.detectedVendorName && (
                  <Chip label={`Dodavatel: ${testResult.detectedVendorName}`} size="small" />
                )}
              </Box>

              <Alert severity="success" sx={{ mb: 2 }}>
                Toto je JSON po OpenAI extrakci a následné validaci. Tento výsledek použijte pro ověření, zda upravená instrukční sada funguje správně.
              </Alert>

              <TextField
                label="Validovaný JSON z dalšího kroku"
                value={JSON.stringify(testResult.validatedData, null, 2)}
                minRows={18}
                multiline
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>OCR text ze vstupního PDF</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    label="OCR text"
                    value={testResult.ocrText}
                    minRows={12}
                    multiline
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </AccordionDetails>
              </Accordion>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Historie zpracování
              </Typography>
              {testResult.statusHistory.map((status, index) => (
                <Typography key={`${status.step}-${index}`} variant="body2" color="text.secondary">
                  {status.step}: {status.message}
                </Typography>
              ))}
            </Paper>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OCRAdaptations;
